import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Table from '@mui/material/Table';

import type { ActionItemSelection } from 'src/types/myFollowUp';
import ErrorRow from 'src/components/followUp/TicketOverviewPage/TicketTable/ErrorRow';
import useFollowUpTable from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpTable/FollowUpTableContext';

import HeaderTableCell from './components/HeaderCell';
import TablePagination from './components/TablePagination';
import FollowUpTableRow from './components/TableRow';

import { styles } from './index.styles';

const TableHeaderMap: Record<string, { columnKey: string; minWidth: string }> =
  {
    id: { columnKey: 'id', minWidth: '7ch' },
    survey: { columnKey: 'survey__name', minWidth: '7ch' },
    followUpOwner: {
      columnKey: 'follow_up_owner',
      minWidth: '18ch',
    },
    created: { columnKey: 'created_at', minWidth: '8ch' },
    lastUpdated: { columnKey: 'last_modified_at', minWidth: '8ch' },
    dueAt: { columnKey: 'due_at', minWidth: '8ch' },
    status: { columnKey: 'status', minWidth: '9ch' },
  };

interface FollowUpTableProps {
  onRowSelect: (selectedRow: ActionItemSelection) => void;
}

const FollowUpTable: FC<FollowUpTableProps> = ({ onRowSelect }) => {
  const { t } = useTranslation('myFollowUp');
  const { actionItems, error, loading, pageSize, offset, setOffset } =
    useFollowUpTable();
  const emptyRows = useMemo(
    () => (loading ? pageSize - 1 : pageSize - actionItems.results.length),
    [actionItems, pageSize, loading],
  );

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Box
        sx={{
          overflow: 'scroll',
          maxWidth: '100%',
        }}
      >
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              {Object.entries(TableHeaderMap).map(
                ([translationKey, { columnKey, minWidth }]) => (
                  <HeaderTableCell
                    key={columnKey}
                    columnKey={columnKey}
                    minWidth={minWidth}
                  >
                    {t(`actionItemHeader.${translationKey}`)}
                  </HeaderTableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {error ? (
              <ErrorRow text={error} />
            ) : loading ? (
              <TableRow>
                <TableCell colSpan={9} sx={styles.loadingCell}>
                  <CircularProgress aria-label="spinner" />
                </TableCell>
              </TableRow>
            ) : actionItems.results.length ? (
              actionItems.results.map((followUp) => (
                <FollowUpTableRow
                  key={followUp.id}
                  {...followUp}
                  onClick={() =>
                    onRowSelect({
                      surveyId: followUp.surveyId,
                      actionId: followUp.id,
                      type: followUp.type,
                    })
                  }
                />
              ))
            ) : (
              <ErrorRow text={t('noTicketsFound')} />
            )}
            {emptyRows > 0 && (
              <TableRow>
                <TableCell colSpan={9} height={48 * emptyRows} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        count={actionItems.count}
        previous={actionItems.previous}
        next={actionItems.next}
        offset={offset}
        setOffset={setOffset}
        pageSize={pageSize}
      />
    </TableContainer>
  );
};

export default FollowUpTable;
